import { useState, useEffect, React } from 'react';
import { useNavigate } from "react-router-dom";
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import { Divider } from '@mui/material';
import { db } from 'lib/firebase';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { registerNewUser, isValidEmail } from 'lib/FirebaseDB';
import { collection, getDocs, doc, getDoc, onSnapshot, query, orderBy } from "firebase/firestore";

import Swal from 'sweetalert2';

import { useSnackbar } from 'notistack';
import SiteWrapper from './home-pages/SiteWrapper';

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
      sx={{
        color: "rgba(255, 255, 255, 0.3)",
      }}
    >
      {"Copyright © "}
      <Link
        color="inherit"
        href="https://ryandymock.com/"
        sx={{
          color: "rgba(255, 255, 255, 0.5)",
          textDecoration: "none",
          transition: "color 0.2s ease",
          "&:hover": {
            color: "#6366f1",
          },
        }}
      >
        RyanDymock.com
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const useFamilyCode = () => {
  const [code, setCode] = useState({});
  useEffect(() => {
    let mounted = true;
    const getFamilyCode = async() => {
      let results = {};
      const docSnapshot = await getDoc(doc(db, "Admin", "code"));
      if (docSnapshot.exists() && mounted) {  
        setCode(docSnapshot.data()['familyCode']);
      }
    };
    getFamilyCode();
    return () => mounted = false;
  }, []);
  return code;
}

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#6366f1",
    },
    secondary: {
      main: "#ec4899",
    },
    background: {
      default: "#07090e",
      paper: "rgba(255, 255, 255, 0.02)",
    },
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
  },
  typography: {
    fontFamily: "'Kanit', sans-serif",
  },
});

export default function SignUpPage() {
    
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const familyCode = useFamilyCode();

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const registration = {
        firstName: data.get('firstName'),
        lastName: data.get('lastName'),
        email: data.get('email'),
        password: data.get('password'),
        familyCode: data.get('familyCode'),
    }
    validateFieldsAndSubmit(registration);
  };

  const showErrorMessage = (message) => {
    enqueueSnackbar(message, {
        variant: 'error',
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        }});
  }

  const validateFieldsAndSubmit = (registration) => {
    if (!isValidEmail(registration.email)) {
      showErrorMessage("Please enter a valid email address");
      return;
    }
    if (registration.password.length < 5) {
      showErrorMessage("Please enter a longer password");
      return;
    }
    if (registration.familyCode.length < 5) {
      showErrorMessage("Incorrect family code");
      return;
    }
    if (registration.firstName.length < 1 || registration.lastName.length < 1) {
      showErrorMessage("Please enter your first and last name");
      return;
    }
    if (registration.familyCode !== familyCode) {
      showErrorMessage("Incorrect family code");
      return;
    }
    submitRegistration(registration);
  }

  const successCallback = (data) => {
    Swal.fire({
      title: "Registration Successful",
      text: "Registration submitted successfully! Check your email for validation.",
      icon: "success",
      background: "#07090e",
      color: "#fff",
      confirmButtonColor: "#6366f1",
      confirmButtonText: "OKAY",
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        confirmButton: "custom-swal-button",
      },
    }).then(() => {
      navigate("/signIn");
    });
  }

  const failureCallback = (data) => {
    showErrorMessage(data);
  }

  const submitRegistration = (registration) => {
    registerNewUser(registration, successCallback, failureCallback);
  }

  const textInputStyle = {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      backgroundColor: "rgba(255, 255, 255, 0.02)",
      borderRadius: "14px",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.08)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6366f1",
        boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.15)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.4)",
      "&.Mui-focused": {
        color: "#6366f1",
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <SiteWrapper>
        <Container
          component="main"
          maxWidth="xs"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "85vh",
            py: 4,
          }}
        >
          <CssBaseline />
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: "24px",
              p: { xs: 3, sm: 5 },
              boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 0 0 1px rgba(255, 255, 255, 0.05)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #6366f1 0%, #ec4899 100%)",
              },
            }}
          >
            {/* Header/Logo Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "20px",
                bgcolor: "rgba(236, 72, 153, 0.1)",
                border: "1px solid rgba(236, 72, 153, 0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ec4899",
                mb: 3,
                boxShadow: "0 0 20px rgba(236, 72, 153, 0.2)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "rotate(-10deg) scale(1.05)",
                },
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: "28px" }} />
            </Box>

            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 800,
                fontFamily: "'Kanit', sans-serif",
                background: "linear-gradient(to right, #fff 60%, rgba(255,255,255,0.7) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                mb: 1,
              }}
            >
              Join the Family
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                mb: 4,
                textAlign: "center",
                fontWeight: 300,
              }}
            >
              Register your account below
            </Typography>

            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: "100%" }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="familyCode"
                    label="Family Code"
                    name="familyCode"
                    autoComplete="family-code"
                    variant="outlined"
                    sx={textInputStyle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider
                    variant="middle"
                    sx={{
                      my: 1,
                      borderColor: "rgba(255, 255, 255, 0.08)",
                      "&::before, &::after": {
                        borderColor: "rgba(255, 255, 255, 0.08)",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    autoComplete="given-name"
                    name="firstName"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    variant="outlined"
                    sx={textInputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    variant="outlined"
                    sx={textInputStyle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    variant="outlined"
                    sx={textInputStyle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    variant="outlined"
                    sx={textInputStyle}
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 4,
                  mb: 3,
                  py: 1.8,
                  borderRadius: "14px",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  letterSpacing: "0.05em",
                  textTransform: "none",
                  background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                  boxShadow: "0 4px 15px rgba(99, 102, 241, 0.3)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background: "linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)",
                    boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Sign Up
              </Button>

              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Link
                    href="/signIn"
                    variant="body2"
                    sx={{
                      color: "#ec4899",
                      textDecoration: "none",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        color: "#f472b6",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Already have an account? Sign in
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Copyright sx={{ mt: 5 }} />
        </Container>
      </SiteWrapper>
    </ThemeProvider>
  );

}